// ts-rest contract for the review round of the Internal Suitability Assessment
// (SEP-243 sections 31, 32, 34 and 41).
//
// Slice 11 locked the content of the review into an immutable
// `SuitabilityAssessmentVersion` at each sign-off. This contract carries the
// two reviews of that locked version, the Desk queue of one investment manager
// and the four Compliance queues, which travel in one read.
//
// A REVIEWER READS, AND A REVIEWER NEVER EDITS. No endpoint here takes a form
// field. A decision body holds a decision and a comment, and nothing else. The
// edit refusal sits on `saveSuitabilityAssessment`, which admits `desk` and
// `im-assistant` alone (acceptance points 37 and 46).
//
// THE PEER READS THE LOCKED VERSION THROUGH THE GRANT.
// `getPeerReviewAssignment` is the one door, and the server opens it only while
// the assignment is `open`. `apps/server/src/lib/suitability/peer-review-grant.ts`
// holds the rules, and ADR-0005 records the exception.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';
import { errorBody } from '../schemas/common';
import { suitabilityAssessmentVersionSchema } from './suitability-assessment';
import { suitabilityCalculationOverrideSchema } from '../suitability-calculation-overrides';
import { suitabilityLifecycleSchema } from '../suitability-assessment-lifecycle';
import {
    COMPLIANCE_CONDITION_MAX_COUNT,
    complianceConditionSchema,
    complianceConditionTextSchema,
} from '../final-approval';
import { vulnerabilityOutcomeSchema } from '../internal-review-branches';

const c = initContract();

// --- The closed value sets (snake_case VALUES, camelCase keys) -------------

export const peerReviewAssignmentStatusSchema = z.enum(['open', 'completed', 'withdrawn']);
export type PeerReviewAssignmentStatus = z.infer<typeof peerReviewAssignmentStatusSchema>;

export const suitabilityPeerReviewDecisionSchema = z.enum(['approve', 'return_for_action']);
export type SuitabilityPeerReviewDecision = z.infer<typeof suitabilityPeerReviewDecisionSchema>;

export const suitabilityComplianceReviewDecisionSchema = z.enum([
    'approve',
    'return_for_information_or_amendment',
    'reject',
]);
export type SuitabilityComplianceReviewDecision = z.infer<
    typeof suitabilityComplianceReviewDecisionSchema
>;

// --- The rows ---------------------------------------------------------------

export const peerReviewAssignmentSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    versionId: z.string(),
    clientId: z.string(),
    /** The `users` row id of the assigned peer investment manager. */
    assignedImUserId: z.string(),
    /** The resolved staff name, for the queue row. Null when it is unknown. */
    assignedImName: z.string().nullable(),
    assignedByUserId: z.string().nullable(),
    assignedByName: z.string().nullable(),
    assignedAt: z.string(),
    status: peerReviewAssignmentStatusSchema,
    completedAt: z.string().nullable(),
    withdrawnAt: z.string().nullable(),
    withdrawalReason: z.string().nullable(),
    /** The version number of the locked version, for the queue row. */
    versionNumber: z.number().int(),
    /** The client name of the case, taken from the locked version snapshot. */
    clientName: z.string(),
});
export type PeerReviewAssignmentDto = z.infer<typeof peerReviewAssignmentSchema>;

export const suitabilityPeerReviewSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    versionId: z.string(),
    assignmentId: z.string(),
    clientId: z.string(),
    reviewerUserId: z.string(),
    reviewerName: z.string().nullable(),
    decision: suitabilityPeerReviewDecisionSchema,
    comments: z.string().nullable(),
    decidedAt: z.string(),
});
export type SuitabilityPeerReviewDto = z.infer<typeof suitabilityPeerReviewSchema>;

export const suitabilityComplianceReviewSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    versionId: z.string(),
    clientId: z.string(),
    reviewerUserId: z.string(),
    reviewerName: z.string().nullable(),
    decision: suitabilityComplianceReviewDecisionSchema,
    comments: z.string().nullable(),
    decidedAt: z.string(),
});
export type SuitabilityComplianceReviewDto = z.infer<typeof suitabilityComplianceReviewSchema>;

// --- The queues -------------------------------------------------------------

/**
 * One row of the Desk queue "Peer Reviews to Complete" (section 41).
 *
 * The row carries the assignment and no review content. The reviewer opens the
 * locked version with `getPeerReviewAssignment`, and the grant is checked
 * there.
 */
export const peerReviewQueueResponseSchema = z.object({
    assignments: z.array(peerReviewAssignmentSchema),
});

/**
 * One flag of the rulebook as the locked version froze it at the sign-off
 * (SEP-293 section 8). The peer agrees with or challenges each Red flag.
 */
export const peerReviewFlagSchema = z.object({
    id: z.string(),
    ruleId: z.string(),
    severity: z.enum(['red', 'amber', 'info']),
    trigger: z.string(),
    challenge: z.string(),
    values: z.record(
        z.string(),
        z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(z.string())]),
    ),
    rationale: z.string().nullable(),
    rationaleAt: z.string().nullable(),
    /** True for a Red flag: the approval needs the choice of the peer. */
    requiresPeerReview: z.boolean(),
    /**
     * True when the server removed health or other special category details
     * from the flag for the peer (SEP-318 decision 3). The rule id, the
     * severity and the challenge stay. The trigger is a neutral text, the
     * values are empty and the rationale is null.
     */
    detailWithheld: z.boolean().optional(),
});
export type PeerReviewFlag = z.infer<typeof peerReviewFlagSchema>;

/**
 * One closed peer review of the signed-in investment manager (SEP-329).
 *
 * The grant ends at the close, so the row holds no review content. It holds the
 * label of the case and the decision that the reviewer recorded themselves.
 */
export const completedPeerReviewSchema = z.object({
    assignmentId: z.string(),
    assessmentId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    versionNumber: z.number().int(),
    assignedByName: z.string().nullable(),
    assignedAt: z.string(),
    decision: suitabilityPeerReviewDecisionSchema,
    comments: z.string().nullable(),
    decidedAt: z.string(),
});
export type CompletedPeerReviewDto = z.infer<typeof completedPeerReviewSchema>;

export const completedPeerReviewsResponseSchema = z.object({
    reviews: z.array(completedPeerReviewSchema),
});

/** One row of the Comply queue "Compliance Reviews to Complete" (section 41). */
export const complianceReviewQueueRowSchema = z.object({
    assessmentId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    versionId: z.string(),
    versionNumber: z.number().int(),
    lifecycle: suitabilityLifecycleSchema,
    signedOffAt: z.string(),
    responsibleImUserId: z.string().nullable(),
    responsibleImName: z.string().nullable(),
    /** The peer decision that put the case in this queue, when there is one. */
    peerReview: suitabilityPeerReviewSchema.nullable(),
    /**
     * The OPEN peer review assignment of the newest version, when there is one.
     *
     * Compliance takes an assignment back through it (section 31). A queue that
     * could not name the open assignment would leave a read grant alive with no
     * way to end it, so the field travels on every row of the Compliance
     * screen.
     */
    openAssignment: peerReviewAssignmentSchema.nullable(),
});
export type ComplianceReviewQueueRow = z.infer<typeof complianceReviewQueueRowSchema>;

/** One review that Compliance already decided (SEP-334, the "Decided" list). */
export const complianceDecidedReviewSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    versionNumber: z.number().int(),
    decision: suitabilityComplianceReviewDecisionSchema,
    reviewerName: z.string().nullable(),
    comments: z.string().nullable(),
    decidedAt: z.string(),
});
export type ComplianceDecidedReviewDto = z.infer<typeof complianceDecidedReviewSchema>;

export const complianceDecidedReviewsResponseSchema = z.object({
    reviews: z.array(complianceDecidedReviewSchema),
});

/**
 * THE WHOLE COMPLIANCE SCREEN IN ONE READ (section 41).
 *
 * Compliance owns every state of the round in which the work sits with the
 * firm, so the screen holds four queues and the read holds four lists. A
 * separate endpoint for each one would make four round trips of one page and
 * would leave a state off the screen whenever a queue was forgotten, which is
 * how a refused review and a review under peer review both became invisible.
 */
export const complianceReviewQueuesResponseSchema = z.object({
    /** Signed off, and waiting for Compliance to name the peer investment manager. */
    awaitingPeerAssignment: z.array(complianceReviewQueueRowSchema),
    /** With a peer investment manager now. Compliance may take the assignment back. */
    inPeerReview: z.array(complianceReviewQueueRowSchema),
    /** The peer approved it, and it waits for the Compliance decision. */
    inComplianceReview: z.array(complianceReviewQueueRowSchema),
    /** Compliance refused it. The return is the ONE door out (section 34). */
    rejected: z.array(complianceReviewQueueRowSchema),
});
export type ComplianceReviewQueuesResponse = z.infer<typeof complianceReviewQueuesResponseSchema>;

/**
 * The full review dossier of one case.
 *
 * `getReviewDossier` serves the Compliance screen: a queue row expands into the
 * locked version it decides, its overrides and every earlier decision.
 * COMPLIANCE MUST READ THE REVIEW BEFORE IT APPROVES IT, and this is the read.
 * It holds each locked version, each override of a derived figure, and each
 * decision of both reviews, so the reader sees the whole history of the case in
 * one read (sections 33 and 34).
 */
export const suitabilityReviewDossierSchema = z.object({
    assessmentId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    lifecycle: suitabilityLifecycleSchema,
    responsibleImUserId: z.string().nullable(),
    responsibleImName: z.string().nullable(),
    /** Newest first. */
    versions: z.array(suitabilityAssessmentVersionSchema),
    calculationOverrides: z.array(suitabilityCalculationOverrideSchema),
    peerReviews: z.array(suitabilityPeerReviewSchema),
    complianceReviews: z.array(suitabilityComplianceReviewSchema),
    assignments: z.array(peerReviewAssignmentSchema),
    /**
     * The Red, Amber and Info flags that the NEWEST locked version froze at the
     * sign-off, with the rationale of the investment manager. Compliance reads
     * them at the top of the signed-off version (SEP-335). Absent when the
     * flags feature is off.
     */
    flags: z.array(peerReviewFlagSchema).optional(),
    /**
     * The conditions that Compliance set on the approval, oldest first, with
     * their status (SEP-318). Absent when the reader cannot see them.
     */
    complianceConditions: z.array(complianceConditionSchema).optional(),
});
export type SuitabilityReviewDossier = z.infer<typeof suitabilityReviewDossierSchema>;

/**
 * The vulnerability position of the client, as the peer sees it (SEP-318
 * decision 3).
 *
 * The values come from the latest COMPLETED vulnerability assessment of the
 * client. The peer sees the status and the support arrangements. The scores,
 * the answers, the reason for the outcome and the health details are for the
 * investment manager and Compliance only (ADR-0016).
 */
export const peerVulnerabilitySummarySchema = z.object({
    /** Null when the client has no completed vulnerability assessment. */
    outcome: vulnerabilityOutcomeSchema.nullable(),
    /** The plain label of the outcome, for example "Potentially vulnerable". */
    outcomeLabel: z.string().nullable(),
    /** "How we will support the client". */
    supportPlan: z.string().nullable(),
    /** The labels of the communication needs: adjustments, not medical facts. */
    communicationNeeds: z.array(z.string()),
});
export type PeerVulnerabilitySummary = z.infer<typeof peerVulnerabilitySummarySchema>;

/**
 * What the assigned peer investment manager sees.
 *
 * The locked version is the WHOLE content, and there is no draft beside it. The
 * overrides travel with it, because section 26 says the reviewer must see the
 * figure that an override replaced.
 */
export const peerReviewWorkspaceSchema = z.object({
    assignment: peerReviewAssignmentSchema,
    version: suitabilityAssessmentVersionSchema,
    calculationOverrides: z.array(suitabilityCalculationOverrideSchema),
    /** The vulnerability status and support arrangements of the client (SEP-318). */
    vulnerabilitySummary: peerVulnerabilitySummarySchema,
    /**
     * The flags of the locked version (SEP-293 section 8), SO6 included
     * (SEP-318 decision 3). Empty when the feature is off.
     */
    flags: z.array(peerReviewFlagSchema).optional(),
    /** SO6 is live: three or more Amber flags. The approval needs the escalation answer. */
    escalationRequired: z.boolean().optional(),
});

/** The choice of the peer on one Red flag (SEP-293 section 8). */
export const peerReviewFlagChoiceSchema = z.object({
    flagId: z.string().min(1),
    outcome: z.enum(['agreed', 'challenged']),
    comment: z.string().max(4000).nullable().optional(),
});
export type PeerReviewFlagChoice = z.infer<typeof peerReviewFlagChoiceSchema>;
export type PeerReviewWorkspace = z.infer<typeof peerReviewWorkspaceSchema>;

export const suitabilityReviewContract = c.router(
    {
        // -- The Desk queue and the peer workspace (sections 31, 32 and 41) --
        listPeerReviewQueue: {
            method: 'GET',
            path: '/desk/suitability/peer-reviews',
            responses: { 200: peerReviewQueueResponseSchema, 403: errorBody },
            summary: 'The open peer review assignments of the signed-in investment manager',
        },
        listCompletedPeerReviews: {
            method: 'GET',
            path: '/desk/suitability/peer-review-history',
            responses: { 200: completedPeerReviewsResponseSchema, 403: errorBody },
            summary: 'The peer reviews that the signed-in investment manager closed',
        },
        getPeerReviewAssignment: {
            method: 'GET',
            path: '/desk/suitability/peer-reviews/:assignmentId',
            pathParams: z.object({ assignmentId: z.string() }),
            responses: { 200: peerReviewWorkspaceSchema, 403: errorBody, 404: errorBody },
            summary: 'The one locked version an open peer review assignment admits',
        },
        recordPeerReview: {
            method: 'POST',
            path: '/desk/suitability/peer-reviews/:assignmentId/decision',
            pathParams: z.object({ assignmentId: z.string() }),
            // NO FORM FIELD. A decision and a comment, and nothing else.
            body: z.object({
                decision: suitabilityPeerReviewDecisionSchema,
                comments: z.string().nullable().optional(),
                // SEP-293 section 8. The choice on each Red flag, and the
                // answer to "Escalate to Compliance?". No form field.
                flagReviews: z.array(peerReviewFlagChoiceSchema).optional(),
                escalation: z
                    .object({
                        escalate: z.boolean(),
                        note: z.string().max(4000).nullable().optional(),
                    })
                    .nullable()
                    .optional(),
            }),
            responses: {
                200: z.object({ review: suitabilityPeerReviewSchema, lifecycle: z.string() }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                // One decision for each assignment. The unique index refuses a
                // second row, and the seam maps it here.
                409: staffActionErrorSchema,
            },
            summary: 'The assigned peer approves or returns the locked Internal Review',
        },

        // -- The Comply queue and the Compliance decision (sections 31 and 34) --
        listComplianceReviewQueues: {
            method: 'GET',
            path: '/comply/suitability/reviews',
            responses: { 200: complianceReviewQueuesResponseSchema, 403: errorBody },
            summary: 'The four Compliance queues of the review round, in one read',
        },
        listComplianceDecidedReviews: {
            method: 'GET',
            path: '/comply/suitability/review-decisions',
            responses: { 200: complianceDecidedReviewsResponseSchema, 403: errorBody },
            summary: 'The reviews that Compliance already decided, newest first',
        },
        assignPeerReview: {
            method: 'POST',
            path: '/comply/suitability/reviews/:assessmentId/assign-peer',
            pathParams: z.object({ assessmentId: z.string() }),
            body: z.object({ assignedImUserId: z.string().min(1) }),
            responses: {
                200: z.object({ assignment: peerReviewAssignmentSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                // Two compliance officers assigned the same version at once.
                // The partial unique index of migration 0179 refuses the
                // second writer, and the seam maps it here.
                409: staffActionErrorSchema,
            },
            summary: 'Compliance names the peer investment manager for one locked version',
        },
        withdrawPeerReviewAssignment: {
            method: 'POST',
            path: '/comply/suitability/peer-assignments/:assignmentId/withdraw',
            pathParams: z.object({ assignmentId: z.string() }),
            body: z.object({ reason: z.string().min(1) }),
            responses: {
                200: z.object({ assignment: peerReviewAssignmentSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Compliance takes an open peer review assignment back',
        },
        recordComplianceReview: {
            method: 'POST',
            path: '/comply/suitability/reviews/:assessmentId/decision',
            pathParams: z.object({ assessmentId: z.string() }),
            body: z.object({
                decision: suitabilityComplianceReviewDecisionSchema,
                comments: z.string().nullable().optional(),
                // SEP-318. The conditions of an approval. Each one becomes a
                // ComplianceCondition that the IM resolves and Compliance
                // verifies. The server refuses conditions on a return and on
                // a refusal.
                conditions: z
                    .array(complianceConditionTextSchema)
                    .max(COMPLIANCE_CONDITION_MAX_COUNT)
                    .optional(),
            }),
            responses: {
                200: z.object({ review: suitabilityComplianceReviewSchema, lifecycle: z.string() }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Compliance approves, returns or refuses the locked Internal Review',
        },

        // -- The whole history of one case (sections 33 and 34) --
        getReviewDossier: {
            method: 'GET',
            path: '/suitability/reviews/:assessmentId/dossier',
            pathParams: z.object({ assessmentId: z.string() }),
            responses: { 200: suitabilityReviewDossierSchema, 403: errorBody, 404: errorBody },
            summary: 'Every locked version, override and review decision of one case',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
